import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Calendar, Clock, User, Video, CheckCircle, XCircle, AlertCircle, Clock as ClockIcon, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { formatTime } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface Booking {
  id: string;
  booking_datetime: string; // UTC ISO timestamp
  booking_timezone?: string;
  proposed_datetime?: string; // UTC ISO timestamp
  duration_minutes: number;
  price: number;
  status: string;
  video_conference_link?: string;
  coach_id?: string;
  coach_feedback?: string;
}

interface Profile {
  full_name: string;
  email: string;
}

const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [coachProfile, setCoachProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: bookingData, error: bookingError } = await supabase
        .from("bookings")
        .select("*")
        .eq("id", bookingId)
        .eq("user_id", user.id)
        .single();

      if (bookingError) throw bookingError;
      
      setBooking(bookingData);

      if (bookingData?.coach_id) {
        const { data: coachData } = await supabase
          .from("profiles")
          .select("full_name, email")
          .eq("id", bookingData.coach_id)
          .single();

        setCoachProfile(coachData);
      }
    } catch (error) {
      console.error("Error fetching booking:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      confirmed: "default",
      denied: "destructive",
      proposed: "outline",
    };
    return <Badge variant={variants[status] || "default"}>{status.toUpperCase()}</Badge>;
  };

  const getStatusInfo = (status: string) => {
    const statusConfig: Record<string, { icon: any; label: string; description: string; color: string }> = {
      pending: {
        icon: ClockIcon,
        label: "Pending",
        description: "Waiting for coach to review your booking request",
        color: "text-yellow-500"
      },
      confirmed: {
        icon: CheckCircle,
        label: "Confirmed",
        description: "Your lesson is confirmed and ready to go",
        color: "text-green-500"
      },
      denied: {
        icon: XCircle,
        label: "Denied",
        description: "Coach is unavailable at the requested time",
        color: "text-red-500"
      },
      proposed: {
        icon: AlertCircle,
        label: "New Time Proposed",
        description: "Coach has suggested an alternative time",
        color: "text-blue-500"
      },
      cancelled: {
        icon: XCircle,
        label: "Cancelled",
        description: "This booking has been cancelled",
        color: "text-red-500"
      }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const handleCancelBooking = async () => {
    if (!booking) return;
    
    setCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-booking', {
        body: { 
          bookingId: booking.id,
          cancellationReason: cancellationReason || "User cancelled"
        }
      });

      if (error) throw error;

      const result = data as { refunded: boolean; cancellationFee: number };
      
      toast({
        title: "Booking Cancelled",
        description: result.refunded 
          ? "Your booking has been cancelled and payment refunded."
          : `Your booking has been cancelled. A $${result.cancellationFee.toFixed(2)} cancellation fee has been charged due to late cancellation.`,
      });

      // Refresh booking details
      await fetchBookingDetails();
      setShowCancelDialog(false);
      setCancellationReason("");
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast({
        title: "Error",
        description: "Failed to cancel booking. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  const canCancelBooking = booking && booking.status !== 'cancelled' && booking.status !== 'denied';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading booking details...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Booking Not Found</CardTitle>
            <CardDescription>This booking doesn't exist or you don't have access to it.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/progress")}>Go to My Bookings</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/progress")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to My Bookings
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">Booking Details</CardTitle>
                <CardDescription>Booking ID: {booking.id.substring(0, 8)}</CardDescription>
              </div>
              {getStatusBadge(booking.status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start gap-3">
              {(() => {
                const statusInfo = getStatusInfo(booking.status);
                const StatusIcon = statusInfo.icon;
                return (
                  <>
                    <StatusIcon className={`h-5 w-5 mt-0.5 ${statusInfo.color}`} />
                    <div>
                      <p className="font-medium">Status</p>
                      <p className={`text-sm font-medium ${statusInfo.color}`}>{statusInfo.label}</p>
                      <p className="text-sm text-muted-foreground">{statusInfo.description}</p>
                    </div>
                  </>
                );
              })()}
            </div>

            <Separator />

            {coachProfile && (
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Coach</p>
                  <p className="text-sm text-muted-foreground">{coachProfile.full_name}</p>
                  <p className="text-sm text-muted-foreground">{coachProfile.email}</p>
                </div>
              </div>
            )}

            <Separator />

            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Date</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(booking.booking_datetime), "EEEE, MMMM d, yyyy")}
                </p>
              </div>
            </div>

                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">Time & Duration</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(booking.booking_datetime), "h:mm a")} ({booking.duration_minutes} minutes)
                    </p>
                  </div>
                </div>

            {booking.proposed_datetime && booking.status === "proposed" && (
              <>
                <Separator />
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="font-medium mb-2">Proposed New Time</p>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(booking.proposed_datetime), "EEEE, MMMM d, yyyy")} at {format(new Date(booking.proposed_datetime), "h:mm a")}
                  </p>
                  <Button
                    onClick={() => navigate(`/proposal/${booking.id}`)}
                    className="mt-4"
                  >
                    Respond to Proposal
                  </Button>
                </div>
              </>
            )}

            <Separator />

            <div className="flex items-start gap-3">
              <div className="h-5 w-5 text-muted-foreground mt-0.5">$</div>
              <div>
                <p className="font-medium">Price</p>
                <p className="text-sm text-muted-foreground">${booking.price}</p>
              </div>
            </div>

            {booking.status === "confirmed" && booking.video_conference_link && (
              <>
                <Separator />
                <div className="flex items-start gap-3">
                  <Video className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium mb-2">Video Conference</p>
                    <Button asChild className="w-full">
                      <a
                        href={booking.video_conference_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Join Lesson
                      </a>
                    </Button>
                  </div>
                </div>
              </>
            )}

            {booking.coach_feedback && (
              <>
                <Separator />
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium mb-2">Coach Feedback</p>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">{booking.coach_feedback}</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {canCancelBooking && (
              <>
                <Separator />
                <Button 
                  variant="destructive" 
                  onClick={() => setShowCancelDialog(true)}
                  className="w-full"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Cancel Booking
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to cancel this booking?</p>
              {booking && (() => {
                const bookingDateTime = new Date(booking.booking_datetime);
                const now = new Date();
                const hoursUntilLesson = (bookingDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
                
                if (hoursUntilLesson >= 24) {
                  return (
                    <p className="text-green-600 font-medium">
                      ✓ Full refund - You're cancelling more than 24 hours in advance.
                    </p>
                  );
                } else {
                  return (
                    <p className="text-red-600 font-medium">
                      ⚠ A $15.00 cancellation fee will be charged as you're cancelling within 24 hours of the lesson.
                    </p>
                  );
                }
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancellation-reason">Reason (optional)</Label>
            <Textarea
              id="cancellation-reason"
              placeholder="Let us know why you're cancelling..."
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Keep Booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelBooking}
              disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelling ? "Cancelling..." : "Cancel Booking"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BookingDetails;
